import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { companiesApi, supportingDataApi } from '@/services/api';
import { Company, Sector, AssetClass, Country } from '@/types/client';
import { useScope } from '@/hooks/useScope';
import { WRITE_GENERAL_MASTERS } from '@/constants/scopeCodes';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import debounce from 'lodash.debounce';
import { PageFrame, PageHeader, Surface, FilterBar, FilterSelect, Button as UiButton, ActiveStatus, RowActionMenu, Placeholder, Note } from '@/components/ui';
import type { FilterOption } from '@/components/ui';
import DataTable, { ColumnDef } from '@/components/utils/DataTable';

const STATUS_OPTIONS: FilterOption[] = [
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

const CompanyList: React.FC = () => {
  const navigate = useNavigate();
  const canWrite = useScope(WRITE_GENERAL_MASTERS);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination & Search
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Sort
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Filters
  const [filterSector, setFilterSector] = useState<Sector | null>(null);
  const [filterAssetClass, setFilterAssetClass] = useState<AssetClass | null>(null);
  const [filterCountry, setFilterCountry] = useState<Country | null>(null);
  const [filterStatus, setFilterStatus] = useState('');      // '' | 'true' | 'false'

  // Supporting data for dropdowns
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [assetClasses, setAssetClasses] = useState<AssetClass[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);

  const sectorOptions: FilterOption[] = useMemo(
    () => sectors.map(s => ({ value: s.id.toString(), label: s.sector_name })),
    [sectors],
  );
  const assetClassOptions: FilterOption[] = useMemo(
    () => assetClasses.map(a => ({ value: a.id.toString(), label: a.name })),
    [assetClasses],
  );
  const countryOptions: FilterOption[] = useMemo(
    () => countries.map(c => ({ value: c.id.toString(), label: `${c.name} (${c.code})` })),
    [countries],
  );
  const asSelected = (opts: FilterOption[], val: string): FilterOption | null =>
    opts.find(o => o.value === val) ?? null;

  // Load dropdown data on mount
  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const fetchAllSectors = async (): Promise<Sector[]> => {
      const all: Sector[] = [];
      let page = 1;
      const pageSize = 100;
      let hasMore = true;
      while (hasMore) {
        const batch = await companiesApi.getSectors({ signal, params: { page, page_size: pageSize } });
        all.push(...batch);
        hasMore = batch.length >= pageSize;
        if (hasMore) page++;
      }
      return all;
    };

    Promise.all([
      fetchAllSectors(),
      companiesApi.getAssetClasses({ signal }),
      supportingDataApi.getCountries({ signal }),
    ]).then(([sects, classes, countryList]) => {
      if (signal.aborted) return;
      setSectors(sects.filter((s) => s.is_active !== false));
      setAssetClasses(classes.filter((a) => a.is_active !== false));
      setCountries(countryList.filter((c) => c.is_active !== false));
    }).catch((err) => {
      if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
        console.error('Failed to load supporting data:', err);
      }
    });

    return () => controller.abort();
  }, []);

  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const ordering = sortOrder === 'desc' ? `-${sortBy}` : sortBy;
      const params: Record<string, any> = {
        page: page + 1,
        page_size: rowsPerPage,
        search: debouncedSearchTerm,
        ordering,
      };
      if (filterSector) params.sector = filterSector.id;
      if (filterAssetClass) params.asset_class = filterAssetClass.id;
      if (filterCountry) params.country = filterCountry.id;
      if (filterStatus !== '') params.is_active = filterStatus === 'true';

      const response = await companiesApi.getCompanies(params);
      setCompanies(response.results);
      setTotalCount(response.count);
      setError(null);
    } catch (err) {
      setError('Failed to fetch companies');
      console.error('Error fetching companies:', err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearchTerm, sortBy, sortOrder, filterSector, filterAssetClass, filterCountry, filterStatus]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const updateSearchQuery = useMemo(
    () => debounce((value: string) => {
      setDebouncedSearchTerm(value);
      setPage(0);
    }, 500),
    []
  );

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(0);
  };

  const handleView = useCallback((company: Company) => {
    navigate(`/companies/${company.id}`);
  }, [navigate]);

  const handleEdit = useCallback((company: Company) => {
    navigate(`/companies/${company.id}/edit`);
  }, [navigate]);

  const clearAllFilters = () => {
    setFilterSector(null);
    setFilterAssetClass(null);
    setFilterCountry(null);
    setFilterStatus('');
    setSearchTerm('');
    updateSearchQuery('');
    setPage(0);
  };

  /* Columns drive the head, the sort arrows and the cells, so this table sorts
     and reads like every other list in the app. */
  const columns = useMemo<ColumnDef<Company>[]>(() => [
    { key: 'symbol', header: 'Symbol', sortable: true, width: '130px', render: r => r.symbol },
    { key: 'name', header: 'Name', sortable: true, width: '260px', render: r => r.name },
    { key: 'isin', header: 'ISIN', sortable: true, width: '160px', render: r => r.isin },
    { key: 'bse_code', header: 'BSE Code', sortable: true, width: '130px', render: r => r.bse_code || '—' },
    { key: 'nse_code', header: 'NSE Code', sortable: true, width: '140px', render: r => r.nse_code || '—' },
    { key: 'sector__sector_name', header: 'Sector', sortable: true, width: '170px',
      render: r => r.sector_name || '—' },
    { key: 'asset_class__class_name', header: 'Asset Class', sortable: true, width: '150px',
      render: r => r.asset_class_name || r.asset_class?.name || '—' },
    { key: 'country', header: 'Country', width: '170px',
      render: r => (r.country ? `${r.country.name} (${r.country.code})` : '—') },
    { key: 'is_active', header: 'Status', sortable: true, width: '120px',
      render: r => <ActiveStatus active={!!r.is_active} /> },
    { key: 'actions', header: 'Actions', width: '90px',
      render: r => (
        <RowActionMenu
          label="actions"
          actions={[
            { label: 'View Details', icon: <VisibilityIcon fontSize="small" />, onClick: () => handleView(r) },
            { label: 'Edit', icon: <EditIcon fontSize="small" />, hidden: !canWrite, onClick: () => handleEdit(r) },
          ]}
        />
      ) },
  ], [canWrite, handleView, handleEdit]);

  // First load and a failed load replace the filters and table, as before —
  // but inside the page frame, so the title stays put. The Add button stays
  // hidden in those states, as it was when they rendered without a header.
  const initialLoading = loading && companies.length === 0 && page === 0 && !searchTerm;
  const blockingError = !initialLoading && error && companies.length === 0;

  return (
    <PageFrame
      header={
        <PageHeader
          title="Companies"
          subtitle="Every listed company OAT can hold, with its exchange codes and sector."
          actions={
            canWrite && !initialLoading && !blockingError ? (
              <UiButton variant="primary" onClick={() => navigate('/companies/new')}>
                Add New Company
              </UiButton>
            ) : undefined
          }
        />
      }
    >
      {initialLoading ? (
        <Surface>
          <Placeholder kind="loading" message="Loading companies..." />
        </Surface>
      ) : blockingError ? (
        <Note tone="error">Error: {error}</Note>
      ) : (
      <>
      <FilterBar
        searchValue={searchTerm}
        onSearchChange={value => { setSearchTerm(value); updateSearchQuery(value); setPage(0); }}
        searchPlaceholder="Search by name, symbol, ISIN, BSE/NSE code…"
        activeFilterCount={[filterSector, filterAssetClass, filterCountry, filterStatus].filter(Boolean).length}
        onClearFilters={clearAllFilters}
        filters={
          <>
              <FilterSelect
                label="Sector"
                value={asSelected(sectorOptions, filterSector?.id.toString() ?? '')}
                onChange={opt => {
                  setFilterSector(opt ? sectors.find(s => s.id.toString() === opt.value) ?? null : null);
                  setPage(0);
                }}
                options={sectorOptions}
              />

              <FilterSelect
                label="Asset Class"
                value={asSelected(assetClassOptions, filterAssetClass?.id.toString() ?? '')}
                onChange={opt => {
                  setFilterAssetClass(opt ? assetClasses.find(a => a.id.toString() === opt.value) ?? null : null);
                  setPage(0);
                }}
                options={assetClassOptions}
              />

              <FilterSelect
                label="Country"
                value={asSelected(countryOptions, filterCountry?.id.toString() ?? '')}
                onChange={opt => {
                  setFilterCountry(opt ? countries.find(c => c.id.toString() === opt.value) ?? null : null);
                  setPage(0);
                }}
                options={countryOptions}
              />

              <FilterSelect
                label="Status"
                value={asSelected(STATUS_OPTIONS, filterStatus)}
                onChange={opt => { setFilterStatus(opt?.value ?? ''); setPage(0); }}
                options={STATUS_OPTIONS}
              />
          </>
        }
      />

      {/* Table */}
      <Surface>
        <DataTable<Company>
        data={companies}
        columns={columns}
        rowKey={row => row.id}
        totalCount={totalCount}
        loading={loading}
        error={error}
        currentPage={page + 1}
        totalPages={Math.max(Math.ceil(totalCount / rowsPerPage), 1)}
        onPageChange={next => setPage(next - 1)}
        pageSize={rowsPerPage}
        sortField={sortBy}
        sortDir={sortOrder}
        onSort={handleSort}
        hideSearch
        embedded
        onRowClick={row => navigate(`/companies/${row.id}`)}
        pageSizeOptions={[10, 20, 50, 100]}
        onPageSizeChange={size => { setRowsPerPage(size); setPage(0); }}
        loadingMessage="Loading companies…"
        emptyMessage="No companies match these filters."
        />
      </Surface>
      </>
      )}
    </PageFrame>
  );
};

export default CompanyList;
